/* eslint-disable react/prop-types */
import React, { useState, useEffect, memo } from 'react';
import { Image, View, ActivityIndicator } from 'react-native';
import { useGetAttachmentByIdQuery } from '../store/api/QueueApi';
import { useTheme } from 'react-native-paper';

/**
 * SecureImage component that fetches images by attachment ID
 * through secure API endpoints with authentication headers.
 */
const SecureImage = ({ attachmentId, style, onError, fallback }) => {
  const theme = useTheme();
  const [imageUri, setImageUri] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Use RTK Query to fetch the attachment by ID
  const {
    data: fileData,
    error,
    isLoading: isQueryLoading,
  } = useGetAttachmentByIdQuery(attachmentId, {
    skip: !attachmentId,
  });

  useEffect(() => {
    if (fileData) {
      try {
        // Convert the file data to a blob URL for display
        const binaryData = fileData.data || fileData;

        if (Array.isArray(binaryData)) {
          // Convert array of numbers to Uint8Array
          const uint8Array = new Uint8Array(binaryData);
          const blob = new Blob([uint8Array], {
            type: fileData.type || 'image/jpeg',
          });
          const blobUrl = URL.createObjectURL(blob);
          setImageUri(blobUrl);
          setHasError(false);
        } else if (binaryData instanceof Blob) {
          const blobUrl = URL.createObjectURL(binaryData);
          setImageUri(blobUrl);
          setHasError(false);
        } else {
          console.error('Unexpected file data format:', typeof binaryData);
          setHasError(true);
        }
      } catch (err) {
        console.error('Error processing secure image data:', err);
        setHasError(true);
      }
      setIsLoading(false);
    }

    if (error) {
      console.error('Error loading secure image:', error);
      setHasError(true);
      setIsLoading(false);
    }
  }, [fileData, error]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (imageUri && imageUri.startsWith('blob:')) {
        URL.revokeObjectURL(imageUri);
      }
    };
  }, [imageUri]);

  if (isLoading || isQueryLoading) {
    return (
      <View style={[style, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  if (hasError || !imageUri) {
    if (fallback) {
      return fallback;
    }
    return null;
  }

  return (
    <Image
      source={{ uri: imageUri }}
      style={style}
      onError={(e) => {
        setHasError(true);
        if (onError) {
          onError(e);
        }
      }}
    />
  );
};

export default memo(SecureImage);
